import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export interface AccountDocument {
  value: string;
  type: 'cpf' | 'cnpj';
}

export interface AccountSituationIE {
  id?: string;
  status?: boolean;
  text?: string;
}

export interface Account {
  id: string;
  document: AccountDocument;
  name?: string;
  rg?: string;
  social_name?: string;
  fantasy_name?: string;
  state_registration?: string;
  municipal_registration?: string;
  setor?: string[];
  status?: string;
  situationIE?: AccountSituationIE;
  typeIE?: string;
  billing_status?: string;
  billing_situation?: string;
  economicGroupId?: string;
  accountType?: string;
  simeiSince?: string;
  natureText?: string;
  created_at: string;
  updated_at?: string;
}

export interface AccountAddress {
  id: string;
  accountId: string;
  city: string;
  state: string;
  street: string;
  district: string;
  number: string;
  zip_code: string;
  complement?: string;
  reference_point?: string;
  type?: string[];
  default_address?: boolean;
}

export interface AccountContactItem {
  id: string;
  contact: string;
  type: 'Celular' | 'Telefone Residencial' | 'Telefone Comercial' | 'Email';
  preferredContact: {
    whatsapp?: boolean;
    phone?: boolean;
    email?: boolean;
  };
}

export interface AccountContact {
  id: string;
  accountId: string;
  name: string;
  contractEnabled: boolean;
  positionOrRelation: string;
  area?: string;
  department?: string | null;
  cpf?: string | null;
  rg?: string | null;
  taxId?: string | null;
  contactFor: string[];
  contactItems: AccountContactItem[];
  originType?: 'api' | 'local' | 'manual';
  created_at?: string;
  updated_at?: string;
}

export interface AccountEconomicGroup {
  id: string;
  economic_group_holding?: {
    name: string;
    taxId: string;
  };
  economic_group_controlled?: Array<{
    name: string;
    taxId: string;
  }>;
}

export interface DocumentsRequestSubject {
  contactId: string;
  contactItemId: string;
  value: string;
  type: string;
}

export interface DocumentsRequestExternalSubject {
  value: string;
  type: string;
}

export interface AccountDocuments {
  accountId: string;
  subjects: DocumentsRequestSubject[];
  externalSubjects: DocumentsRequestExternalSubject[];
}

/**
 * @deprecated Use accountUploadApi.list() instead for file uploads
 */
export interface AccountFile {
  id: string;
  type: string;
  extension: string;
  created_at: string;
  updated_at: string;
  sizeInBytes: number;
  description?: string;
  label: string;
  name: string;
  bucketInfo?: { key?: string; [key: string]: unknown };
  contentType?: string;
  etag?: string;
}

export interface AccountQueryParams {
  economicGroupId?: string;
  page?: number;
  limit?: number;
  search?: string;
  document_type?: 'cpf' | 'cnpj';
  billing_status?: string;
  billing_situation?: string;
  sector?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  pages: number;
  limit: number;
}

export const accountApi = {
  findMany: async (params?: AccountQueryParams): Promise<PaginatedResponse<Account>> => {
    const { data } = await api.get<ApiPaginatedResponse<Account>>('/commercial/accounts', {
      params,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<Account> => {
    const { data } = await api.get<ApiResponse<Account>>(`/commercial/accounts/${id}`);
    return data.data;
  },

  create: async (
    account: Omit<Account, 'id' | 'created_at'>,
  ): Promise<{ success: boolean; id: string }> => {
    const { data } = await api.post<ApiResponse<{ success: boolean; id: string }>>(
      '/commercial/accounts',
      account,
    );
    return data.data;
  },

  update: async (id: string, payload: Partial<Account>): Promise<Account> => {
    const { data } = await api.patch<ApiResponse<Account>>(`/commercial/accounts/${id}`, payload);
    return data.data;
  },

  // Addresses
  findAddresses: async (accountId: string): Promise<AccountAddress[]> => {
    const { data } = await api.get<ApiResponse<AccountAddress[]>>(`/commercial/addresses`, {
      params: { accountId },
    });
    return data.data;
  },

  createAddress: async (address: Omit<AccountAddress, 'id'>): Promise<AccountAddress> => {
    const { data } = await api.post<ApiResponse<AccountAddress>>('/commercial/addresses', address);
    return data.data;
  },

  updateAddress: async (id: string, address: Partial<AccountAddress>): Promise<AccountAddress> => {
    const { data } = await api.put<ApiResponse<AccountAddress>>(
      `/commercial/addresses/${id}`,
      address,
    );
    return data.data;
  },

  deleteAddress: async (id: string): Promise<void> => {
    await api.delete(`/commercial/addresses/${id}`);
  },

  // Contacts
  findContacts: async (accountId: string, page = 1, limit = 100): Promise<AccountContact[]> => {
    const { data } = await api.get<ApiResponse<AccountContact[]>>(`/commercial/contacts`, {
      params: { accountId, page, limit },
    });
    return data.data;
  },

  createContact: async (contact: Omit<AccountContact, 'id'>): Promise<AccountContact> => {
    const { data } = await api.post<ApiResponse<AccountContact>>('/commercial/contacts', contact);
    return data.data;
  },

  updateContact: async (id: string, contact: Partial<AccountContact>): Promise<AccountContact> => {
    const { data } = await api.put<ApiResponse<AccountContact>>(
      `/commercial/contacts/${id}`,
      contact,
    );
    return data.data;
  },

  deleteContact: async (id: string): Promise<void> => {
    await api.delete(`/commercial/contacts/${id}`);
  },

  // Account Documents (subjects only - files are now in account_uploads)
  findDocuments: async (accountId: string): Promise<AccountDocuments | null> => {
    try {
      const { data } = await api.get<ApiResponse<AccountDocuments>>(
        `/commercial/accounts/${accountId}/documents`,
      );
      return data.data;
    } catch {
      return null;
    }
  },

  // Economic Group Vinculation (atomic)
  updateEconomicGroupVinculation: async (
    accountId: string,
    economicGroupId: string | null,
  ): Promise<{ success: boolean }> => {
    const { data } = await api.patch<ApiResponse<{ success: boolean }>>(
      `/commercial/accounts/${accountId}/economic-group`,
      {
        economicGroupId,
      },
    );
    return data.data;
  },

  // Economic Group
  findEconomicGroup: async (id: string): Promise<AccountEconomicGroup | null> => {
    try {
      const { data } = await api.get<ApiResponse<AccountEconomicGroup>>(
        `/commercial/economic-groups/${id}`,
      );
      return data.data;
    } catch {
      return null;
    }
  },

  updateEconomicGroup: async (
    id: string,
    payload: Partial<AccountEconomicGroup>,
  ): Promise<AccountEconomicGroup> => {
    const { data } = await api.put<ApiResponse<AccountEconomicGroup>>(
      `/commercial/economic-groups/${id}`,
      payload,
    );
    return data.data;
  },

  createEconomicGroup: async (
    payload: Omit<AccountEconomicGroup, 'id'>,
  ): Promise<AccountEconomicGroup> => {
    const { data } = await api.post<ApiResponse<AccountEconomicGroup>>(
      '/commercial/economic-groups',
      payload,
    );
    return data.data;
  },
};
